'use client';

import { useState, useEffect } from 'react';

interface SWState {
  offlineReady: boolean;
  needsUpdate: boolean;
  updateSW: () => void;
}

export function useRegisterSW(): SWState {
  const [offlineReady, setOfflineReady] = useState(false);
  const [needsUpdate, setNeedsUpdate] = useState(false);
  const [waitingSW, setWaitingSW] = useState<ServiceWorker | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!('serviceWorker' in navigator)) return;
    if (process.env.NODE_ENV !== 'production') return;

    let registration: ServiceWorkerRegistration | null = null;

    const onMessage = (event: MessageEvent) => {
      if (event.data?.type === 'OFFLINE_READY') {
        setOfflineReady(true);
      }
    };
    navigator.serviceWorker.addEventListener('message', onMessage);

    const onControllerChange = () => {
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);

    navigator.serviceWorker
      .register('/sw.js', { scope: '/' })
      .then((reg) => {
        registration = reg;

        // SW ya instalado y activo — notificar al cliente directamente
        if (reg.active && !navigator.serviceWorker.controller) {
          setOfflineReady(true);
        }

        // Hay un SW esperando (update disponible)
        if (reg.waiting) {
          setWaitingSW(reg.waiting);
          setNeedsUpdate(true);
        }

        reg.addEventListener('updatefound', () => {
          const newWorker = reg.installing;
          if (!newWorker) return;
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              setWaitingSW(newWorker);
              setNeedsUpdate(true);
            }
            if (newWorker.state === 'activated' && !navigator.serviceWorker.controller) {
              setOfflineReady(true);
            }
          });
        });
      })
      .catch((err) => {
        console.warn('[SW] Error al registrar service worker:', err);
      });

    return () => {
      navigator.serviceWorker.removeEventListener('message', onMessage);
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
    };
  }, []);

  const updateSW = () => {
    if (waitingSW) {
      waitingSW.postMessage({ type: 'SKIP_WAITING' });
    }
  };

  return { offlineReady, needsUpdate, updateSW };
}
